/**
 * ConceptHierarchy — collapsible containment tree (unit → group → concept) over the
 * concept registry, with an inline detail panel per concept showing its prerequisite
 * (knowledge-dependency) and adjacency edges plus grade/exam scope.
 *
 * Renders from a flat Concept[] using `parentConceptId` (same reconstruction pattern as
 * ModulesTableView), so no dedicated hierarchy endpoint is needed.
 * See docs/FeatureDesigns/problem-type-similarity.md §4.
 */
import { useMemo, useState } from 'react';
import type { Concept, ProblemType, ProblemTypeRelation } from '../types';

const levelRank = (c: Concept): number =>
  c.level === 'unit' ? 0 : c.level === 'group' ? 1 : 2;

const bandColor = (b?: string): string =>
  b === 'easy' ? '#15803d' : b === 'hard' ? '#b91c1c' : b === 'medium' ? '#b45309' : 'var(--text-secondary)';

// colours for the typed navigation edges
const relColor: Record<string, string> = {
  'harder-variant': '#b91c1c', 'easier-variant': '#15803d',
  'prerequisite': '#7c3aed', 'follow-up': '#0369a1', 'similar-concept': '#b45309',
};
const REL_ORDER = ['harder-variant', 'easier-variant', 'prerequisite', 'follow-up', 'similar-concept'];

export function ConceptHierarchy({
  concepts,
  problemTypes = [],
  relations = [],
  onEdit,
}: {
  concepts: Concept[];
  problemTypes?: ProblemType[];
  relations?: ProblemTypeRelation[];
  onEdit?: (c: Concept) => void;
}) {
  const byId = useMemo(() => new Map(concepts.map(c => [c.id, c])), [concepts]);
  const nameById = (id: string) => byId.get(id)?.name ?? id;

  // problem types grouped under their anchor concept (biggest first)
  const typesByConcept = useMemo(() => {
    const m = new Map<string, ProblemType[]>();
    for (const t of problemTypes) {
      if (!t.primaryConceptId) continue;
      (m.get(t.primaryConceptId) ?? m.set(t.primaryConceptId, []).get(t.primaryConceptId)!).push(t);
    }
    for (const list of m.values()) list.sort((a, b) => (b.size ?? 0) - (a.size ?? 0));
    return m;
  }, [problemTypes]);

  // type id → short label (drop the "concept · band · " prefix for display)
  const typeById = useMemo(() => new Map(problemTypes.map(t => [t.id, t])), [problemTypes]);
  const typeLabel = (id: string) => {
    const t = typeById.get(id);
    const l = t?.label ?? id;
    return l.split(' · ').slice(2).join(' · ') || l;
  };
  // outgoing navigation edges per from-type, grouped by relation and confidence-sorted
  const edgesByFromType = useMemo(() => {
    const m = new Map<string, ProblemTypeRelation[]>();
    for (const e of relations) {
      (m.get(e.fromTypeId) ?? m.set(e.fromTypeId, []).get(e.fromTypeId)!).push(e);
    }
    for (const list of m.values()) list.sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0));
    return m;
  }, [relations]);

  // children grouped by parent; roots = nodes with no (resolvable) parent.
  const { roots, childrenOf } = useMemo(() => {
    const childrenOf = new Map<string, Concept[]>();
    const roots: Concept[] = [];
    for (const c of concepts) {
      const pid = c.parentConceptId;
      if (pid && byId.has(pid)) {
        (childrenOf.get(pid) ?? childrenOf.set(pid, []).get(pid)!).push(c);
      } else {
        roots.push(c);
      }
    }
    const sortNodes = (a: Concept, b: Concept) =>
      levelRank(a) - levelRank(b) || a.name.localeCompare(b.name);
    roots.sort(sortNodes);
    for (const list of childrenOf.values()) list.sort(sortNodes);
    return { roots, childrenOf };
  }, [concepts, byId]);

  // descendant concept count (leaves) for a subtree — shown as the roll-up size.
  const leafCount = useMemo(() => {
    const memo = new Map<string, number>();
    const walk = (c: Concept): number => {
      if (memo.has(c.id)) return memo.get(c.id)!;
      const kids = childrenOf.get(c.id);
      const n = !kids || kids.length === 0 ? 1 : kids.reduce((s, k) => s + walk(k), 0);
      memo.set(c.id, n);
      return n;
    };
    for (const r of roots) walk(r);
    return memo;
  }, [roots, childrenOf]);

  // units expanded by default; groups/concepts collapsed.
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(roots.filter(r => r.level === 'unit' || childrenOf.has(r.id)).map(r => r.id)),
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [openTypeId, setOpenTypeId] = useState<string | null>(null);   // expanded type → shows its edges
  const toggle = (id: string) =>
    setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const expandAll = () => setExpanded(new Set(concepts.map(c => c.id)));
  const collapseAll = () => setExpanded(new Set(roots.map(r => r.id)));

  const badge = (text: string, tone: 'adv' | 'main' | 'grade') => (
    <span key={text} style={{
      fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4, marginLeft: 4,
      color: '#fff',
      background: tone === 'adv' ? '#7c3aed' : tone === 'main' ? '#0369a1' : 'var(--text-secondary)',
    }}>{text}</span>
  );

  const renderNode = (c: Concept, depth: number): React.ReactNode => {
    const kids = childrenOf.get(c.id);
    const hasKids = !!kids && kids.length > 0;
    const isOpen = expanded.has(c.id);
    const isConcept = (c.level ?? 'concept') === 'concept';
    const isSelected = selectedId === c.id;
    return (
      <div key={c.id}>
        <div
          onClick={() => (hasKids ? toggle(c.id) : (setSelectedId(isSelected ? null : c.id)))}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
            padding: '3px 6px', paddingLeft: 6 + depth * 18,
            borderRadius: 5, background: isSelected ? 'var(--bg-hover)' : 'transparent',
            fontWeight: c.level === 'unit' ? 700 : c.level === 'group' ? 600 : 400,
            fontSize: c.level === 'unit' ? 14 : 13,
          }}
        >
          <span style={{ width: 12, color: 'var(--text-secondary)', fontSize: 10 }}>
            {hasKids ? (isOpen ? '▾' : '▸') : ''}
          </span>
          <span style={{ flex: 1 }}>{c.name}</span>
          {isConcept && (c.examScope ?? []).includes('JEE-Advanced') && !(c.examScope ?? []).includes('JEE-Main') && badge('A', 'adv')}
          {isConcept && (c.examScope ?? []).includes('JEE-Main') && !(c.examScope ?? []).includes('JEE-Advanced') && badge('M', 'main')}
          {hasKids && <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>{leafCount.get(c.id)}</span>}
          {isConcept && typesByConcept.get(c.id)?.length ? (
            <span style={{ fontSize: 10, color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '0 5px' }}>
              {typesByConcept.get(c.id)!.length} pt
            </span>
          ) : null}
          {isConcept && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit?.(c); }}
              style={{ fontSize: 11, border: 'none', background: 'transparent', color: 'var(--color-primary)', cursor: 'pointer' }}
            >edit</button>
          )}
        </div>

        {isConcept && isSelected && (
          <div style={{
            marginLeft: 6 + depth * 18 + 18, margin: '2px 0 6px', padding: '8px 10px',
            borderLeft: '2px solid var(--border-color)', fontSize: 12, color: 'var(--text-secondary)',
            display: 'grid', gap: 4,
          }}>
            {c.description && <div style={{ color: 'var(--text-primary)' }}>{c.description}</div>}
            <div><b>Grade/exam:</b> {(c.grades ?? []).join('/') || '—'} · {(c.examScope ?? []).join(', ') || '—'}</div>
            <div><b>Prerequisites:</b> {(c.prerequisiteConceptIds ?? []).length
              ? (c.prerequisiteConceptIds ?? []).map(nameById).join(' · ') : '—'}</div>
            <div><b>Related:</b> {(c.relatedConceptIds ?? []).length
              ? (c.relatedConceptIds ?? []).map(nameById).join(' · ') : '—'}</div>
            {typesByConcept.get(c.id)?.length ? (
              <div>
                <b>Problem types ({typesByConcept.get(c.id)!.length}):</b>
                <ul style={{ margin: '3px 0 0', paddingLeft: 4, listStyle: 'none' }}>
                  {typesByConcept.get(c.id)!.map(t => {
                    const edges = edgesByFromType.get(t.id) ?? [];
                    const isTypeOpen = openTypeId === t.id;
                    return (
                      <li key={t.id} style={{ padding: '1px 0' }}>
                        <div
                          onClick={(e) => { e.stopPropagation(); setOpenTypeId(isTypeOpen ? null : t.id); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: edges.length ? 'pointer' : 'default' }}
                        >
                          <span style={{ width: 10, color: 'var(--text-secondary)', fontSize: 9, flex: 'none' }}>
                            {edges.length ? (isTypeOpen ? '▾' : '▸') : ''}
                          </span>
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: bandColor(t.difficultyBand), flex: 'none' }} />
                          <span style={{ color: 'var(--text-primary)' }}>{typeLabel(t.id)}</span>
                          {edges.length ? (
                            <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>· {edges.length} edges</span>
                          ) : null}
                          <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>{t.size}</span>
                        </div>
                        {isTypeOpen && edges.length > 0 && (
                          <div style={{ margin: '2px 0 4px 20px', display: 'grid', gap: 2 }}>
                            {REL_ORDER.filter(r => edges.some(e => e.relationType === r)).map(rel => (
                              <div key={rel}>
                                {edges.filter(e => e.relationType === rel).map(e => (
                                  <div key={e.id} style={{
                                    borderLeft: `3px solid ${relColor[rel] ?? '#888'}`, paddingLeft: 6, margin: '2px 0',
                                  }}>
                                    <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                                      <span style={{ color: relColor[rel] ?? '#888', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', flex: 'none' }}>
                                        {rel}{e.bandDelta != null ? ` Δ${e.bandDelta}` : ''}
                                      </span>
                                      <span style={{ color: 'var(--text-primary)' }}>→ {typeLabel(e.toTypeId)}</span>
                                      {e.confidence != null && (
                                        <span style={{ marginLeft: 'auto', fontSize: 10, fontVariantNumeric: 'tabular-nums' }}>{e.confidence.toFixed(2)}</span>
                                      )}
                                    </div>
                                    {e.reason && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{e.reason}</div>}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        )}

        {hasKids && isOpen && kids!.map(k => renderNode(k, depth + 1))}
      </div>
    );
  };

  if (concepts.length === 0) {
    return <div style={{ padding: 20, color: 'var(--text-secondary)' }}>No concepts for this subject yet.</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, margin: '0 0 8px', fontSize: 12 }}>
        <button onClick={expandAll} style={linkBtn}>Expand all</button>
        <button onClick={collapseAll} style={linkBtn}>Collapse all</button>
        <span style={{ color: 'var(--text-secondary)' }}>
          {roots.length} units · {concepts.filter(c => c.level === 'group').length} groups · {concepts.filter(c => (c.level ?? 'concept') === 'concept').length} concepts
        </span>
      </div>
      <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, padding: 8, maxHeight: '70vh', overflowY: 'auto' }}>
        {roots.map(r => renderNode(r, 0))}
      </div>
    </div>
  );
}

const linkBtn: React.CSSProperties = {
  border: 'none', background: 'transparent', color: 'var(--color-primary)', cursor: 'pointer', fontSize: 12, padding: 0,
};
